import {
  ExperienceStep,
  FilteredGuest,
  SelectionGroup,
  Selections,
  SpaPackageBasketItem,
} from '@features/DynamicPackagesConfigurationWizard/types';
import moment from 'moment';
import { OvernightPackageSpaExtras } from '../types/SpaExtra.schema';
import { MappedLoggedGuestData } from './addToBasket';
import { filterIncompleteGuests, isGuestInFilteredList, parseGuestDetails } from './guest.helpers';

export function mapSpaExtrasToSelectionGroups(
  spaExtras: OvernightPackageSpaExtras,
  filteredGuests: FilteredGuest[]
): SelectionGroup[] {
  const selections = (spaExtras || []).reduce((selections, extra) => {
    extra.selectedGuestDetails.forEach((guest) => {
      if (isGuestInFilteredList(filteredGuests, guest)) {
        const matchingFilteredGuest = filteredGuests.find(
          (g) =>
            g.firstName === guest.firstName &&
            g.lastName === guest.lastName &&
            g.title === guest.title
        );
        selections.push({
          extra: extra.extra,
          guest_details: parseGuestDetails(matchingFilteredGuest),
        });
      }
    });
    return selections;
  }, [] as Selections);
  return [{ is_extras: true, selections }];
}

function mapSpaExperiencesToConfigGroups(
  spaExperiences: ExperienceStep,
  filteredGuests: FilteredGuest[]
): SelectionGroup[] {
  const selectionGroups: SelectionGroup[] = [];
  const addedGroupIds = new Set<number | string>();
  const flattenedTimeslots = spaExperiences.guestOptions.map((options) => {
    return options.timeslots.flat();
  });
  flattenedTimeslots[0].forEach((timeslot) => {
    const optionGroupId = timeslot?.optionGroupId;

    if (optionGroupId && !addedGroupIds.has(optionGroupId)) {
      selectionGroups.push({
        configuration_group: { id: Number(optionGroupId) },
        selections: [],
      });
      addedGroupIds.add(optionGroupId);
    }
  });
  if (spaExperiences.guestsHaveDifferentTimes && filteredGuests.length > 1) {
    flattenedTimeslots.forEach((option, index) => {
      option.forEach((timeslot) => {
        const mappedGuestDetails = [filteredGuests[index]].map(parseGuestDetails);
        if (timeslot) {
          const { optionGroupId, optionId, startTime, endTime, visitDate } = timeslot;
          const selectionGroup = selectionGroups.find(
            (grp) => Number(grp.configuration_group?.id) === Number(optionGroupId)
          );
          if (selectionGroup) {
            selectionGroup.selections.push({
              option: {
                id: Number(optionId),
              },
              start_time: startTime,
              end_time: endTime,
              guest_details: mappedGuestDetails[index],
              start_date: moment(visitDate).format('YYYY-MM-DD'),
              end_date: moment(visitDate).format('YYYY-MM-DD'),
            });
          }
        }
      });
    });
  } else {
    flattenedTimeslots[0].forEach((timeslot) => {
      if (timeslot) {
        const { optionGroupId, optionId, startTime, endTime, visitDate } = timeslot;
        const selectionGroup = selectionGroups.find(
          (grp) => Number(grp.configuration_group?.id) === Number(optionGroupId)
        );
        if (selectionGroup) {
          filteredGuests.forEach((_guest, index) => {
            const mappedGuestDetails = [filteredGuests[index]].map(parseGuestDetails);
            selectionGroup.selections.push({
              option: {
                id: Number(optionId),
              },
              start_time: startTime,
              end_time: endTime,
              start_date: moment(visitDate).format('YYYY-MM-DD'),
              end_date: moment(visitDate).format('YYYY-MM-DD'),
              guest_details:
                mappedGuestDetails.length === 1 ? mappedGuestDetails[0] : mappedGuestDetails,
            });
          });
        }
      }
    });
  }
  return selectionGroups;
}

function mapRepeatableSpaExperiencesToConfigGroupsByNight(
  spaExperiences: ExperienceStep,
  filteredGuests: FilteredGuest[]
): SelectionGroup[][] {
  // Collect unique dates from all timeslots
  const uniqueDates = new Set<string>();
  spaExperiences.guestOptions.forEach((guestOption) => {
    guestOption.timeslots.flat().forEach((timeslot) => {
      if (timeslot?.visitDate) {
        uniqueDates.add(moment(timeslot.visitDate).format('YYYY-MM-DD'));
      }
    });
  });

  const nightlyGroups: SelectionGroup[][] = [];

  // For each unique date, create one basket item with all config groups for that day
  uniqueDates.forEach((dateKey) => {
    const experiencesForDate: ExperienceStep = {
      ...spaExperiences,
      guestOptions: spaExperiences.guestOptions.map((guestOption) => ({
        ...guestOption,
        timeslots: guestOption.timeslots.map((timeslotArray) =>
          timeslotArray.filter((timeslot) => {
            if (!timeslot?.visitDate) return false;
            return moment(timeslot.visitDate).format('YYYY-MM-DD') === dateKey;
          })
        ),
      })),
    };

    const selectionGroups = mapSpaExperiencesToConfigGroups(experiencesForDate, filteredGuests);
    nightlyGroups.push(selectionGroups);
  });

  return nightlyGroups;
}

export function parseSpaExperiencesForBasket(
  spaExperiences: ExperienceStep,
  spaExtras: OvernightPackageSpaExtras,
  spaPackageId: number | string,
  fromDate: string,
  toDate: string,
  arrivalTime: string,
  endTime: string,
  maxGuestsPerExperience: number,
  loggedInUserGuestData: MappedLoggedGuestData | null,
  isPackageRepeating: boolean,
  parentBasketId?: number
): SpaPackageBasketItem[] {
  const { guestDetails } = spaExperiences;
  const filteredGuests = filterIncompleteGuests(guestDetails, loggedInUserGuestData);

  if (filteredGuests.length > maxGuestsPerExperience) {
    return filteredGuests
      .map((guest, index) => {
        const mappedSpaExperiences = {
          ...spaExperiences,
          guestOptions: [
            spaExperiences.guestOptions[spaExperiences.guestsHaveDifferentTimes ? index : 0],
          ],
          guestDetails: [filteredGuests[index]],
        };

        const mappedSelectionGroups = isPackageRepeating
          ? mapRepeatableSpaExperiencesToConfigGroupsByNight(mappedSpaExperiences, [guest])
          : [mapSpaExperiencesToConfigGroups(mappedSpaExperiences, [guest])];

        const mappedExtraSelectionGroups = mapSpaExtrasToSelectionGroups(spaExtras, [guest]);
        return mappedSelectionGroups
          .map((grp) => {
            const firstSelection = grp[0]?.selections?.[0];
            const visitDateFromSelection =
              firstSelection && 'start_date' in firstSelection && firstSelection.start_date
                ? firstSelection.start_date
                : moment(fromDate).format('YYYY-MM-DD');

            const isRepeatableAndAllOnSameDay =
              isPackageRepeating &&
              grp.every((group) =>
                group.selections.every(
                  (sel) => 'start_date' in sel && sel.start_date === visitDateFromSelection
                )
              );

            const checkOutDate = isRepeatableAndAllOnSameDay
              ? visitDateFromSelection
              : isPackageRepeating
              ? moment(visitDateFromSelection).add(1, 'day').format('YYYY-MM-DD')
              : moment(toDate).format('YYYY-MM-DD');

            return {
              type: 'spa-packages',
              spa_package: {
                id: Number(spaPackageId),
              },
              check_in: `${visitDateFromSelection}  ${arrivalTime}`,
              check_out: `${checkOutDate}  ${endTime}`,
              guest_details: [parseGuestDetails(guest)],
              dynamic_package: 'child',
              package_basket_item_id: parentBasketId,
              selection_groups: [...grp, ...mappedExtraSelectionGroups],
            };
          })
          .flat();
      })
      .flat();
  } else {
    const mappedSelectionGroups = isPackageRepeating
      ? mapRepeatableSpaExperiencesToConfigGroupsByNight(spaExperiences, filteredGuests)
      : [mapSpaExperiencesToConfigGroups(spaExperiences, filteredGuests)];
    const mappedExtraSelectionGroups = mapSpaExtrasToSelectionGroups(spaExtras, filteredGuests);

    return mappedSelectionGroups
      .map((grp) => {
        const firstSelection = grp[0]?.selections?.[0];
        const visitDateFromSelection =
          firstSelection && 'start_date' in firstSelection && firstSelection.start_date
            ? firstSelection.start_date
            : moment(fromDate).format('YYYY-MM-DD');

        // For repeatable packages, check if experiences span multiple days
        // If all on same day: check_in = check_out. If spanning days: check_out = check_in + 1 day
        // For non-repeatable packages, check_out is the end of the entire stay
        const hasMultipleDays = grp.some((group) =>
          group.selections.some(
            (sel) => 'start_date' in sel && sel.start_date !== visitDateFromSelection
          )
        );
        const isRepeatableAndAllOnSameDay = isPackageRepeating && !hasMultipleDays;

        const checkOutDate = isRepeatableAndAllOnSameDay
          ? visitDateFromSelection
          : isPackageRepeating
          ? moment(visitDateFromSelection).add(1, 'day').format('YYYY-MM-DD')
          : moment(toDate).format('YYYY-MM-DD');

        return {
          type: 'spa-packages',
          spa_package: {
            id: Number(spaPackageId),
          },
          check_in: `${visitDateFromSelection}  ${arrivalTime}`,
          check_out: `${checkOutDate}  ${endTime}`,
          guest_details: filteredGuests.map(parseGuestDetails),
          dynamic_package: 'child',
          ...(parentBasketId ? { package_basket_item_id: parentBasketId } : {}),
          selection_groups: [...grp, ...mappedExtraSelectionGroups],
        };
      })
      .flat();
  }
}
